import { useRef, useState } from "react";
import {
motion,
useScroll,
useTransform,
} from "framer-motion";
import { MapPin, Clock, ArrowRight } from "lucide-react";
import { useSEO } from "../components/SEO";
import {
Reveal,
MaskedLines,
ChapterHeading,
} from "../components/AnimatedSection";
import {
Accordion,
AccordionItem,
AccordionTrigger,
AccordionContent,
} from "../components/ui/accordion";
import { values, gallery, roles } from "../data/careers";
import { SITE } from "../data/site";

const Career = () => {
const [role, setRole] = useState("");

const heroRef = useRef(null);

const { scrollYProgress } = useScroll({
target: heroRef,
offset: ["start start", "end start"],
});

const imgY = useTransform(
scrollYProgress,
[0, 1],
["0%", "22%"]
);

useSEO(
"Career | Social Wavez Technologies",
"We're engineers, designers and automation specialists building real systems for premium businesses — join the team."
);

/*

* Get HR email safely.
*
* Update this depending on your SITE object.
  */
  const hrEmail =
  SITE?.hr_email ||
  SITE?.hrMail ||
  SITE?.email ||
  "[hr@socialwavez.com](mailto:hr@socialwavez.com)";

/*

* Creates a Gmail Compose URL.
*
* This opens Gmail directly in the browser instead of relying
* on the operating system's mailto handler.
  */
  const getGmailUrl = (title = "") => {
  const subject = title
  ? `Application: ${title} — Social Wavez Careers`
  : "Career Application — Social Wavez";


const body = title



  ? `Hi Social Wavez Team,


I'm applying for the ${title} position.

Please find my portfolio and details attached.

Best regards,
[Your Name]`      :`Hi Social Wavez Team,

I'm interested in joining Social Wavez.

Please find my portfolio and details attached.

Best regards,
[Your Name]`;


const params = new URLSearchParams({
  view: "cm",
  fs: "1",
  to: hrEmail,
  su: subject,
  body: body,
});

return `https://mail.google.com/mail/?${params.toString()}`;


};

return (
<>
{/* Hero */} <section
     ref={heroRef}
     className="relative overflow-hidden pb-28 pt-44 md:pb-40 md:pt-60"
   >
<motion.img
src="https://images.pexels.com/photos/23496662/pexels-photo-23496662.jpeg?auto=compress&cs=tinysrgb&w=1800"
alt="The Social Wavez team collaborating in a modern workspace"
style={{ y: imgY }}
className="absolute inset-0 h-[120%] w-full object-cover"
/>


    <div className="absolute inset-0 bg-gradient-to-b from-[#07130C]/85 via-[#07130C]/75 to-[#07130C]" />

    <div className="relative mx-auto max-w-7xl px-6 md:px-12">
      <Reveal>
        <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.35em] text-white/60">
          <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-[#3CBE6E] to-[#D3B74D]" />
          Join the Team
        </p>
      </Reveal>

      <h1 className="mt-7 max-w-4xl font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
        <MaskedLines
          trigger="mount"
          delay={0.25}
          lines={[
            "Build Systems That Actually",
            <span
              key="c2"
              className="text-gradient-aurora"
            >
              Move Businesses Forward.
            </span>,
          ]}
        />
      </h1>

      <Reveal delay={0.45}>
        <p className="mt-7 max-w-2xl text-base leading-relaxed text-white/65 md:text-lg">
          We're engineers, designers and automation specialists building
          real systems for premium businesses — and we're always looking
          for people who care about doing it properly.
        </p>
      </Reveal>
    </div>
  </section>

  {/* Values */}
  <section className="bg-white py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <ChapterHeading
        no="01"
        kicker="How We Work"
        titleLines={["Four Things We Don't Compromise On."]}
      />

      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {values.map((v, i) => (
          <Reveal key={v.title} delay={i * 0.1}>
            <div
              data-testid={`value-card-${i}`}
              className="h-full rounded-3xl border border-[#07130C]/10 bg-[#F8FBF6] p-8 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgba(14,58,40,0.14)]"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#1C7543] to-[#D3B74D] text-white">
                <v.icon className="h-5 w-5" />
              </span>

              <h3 className="mt-6 font-display text-lg font-semibold text-[#07130C]">
                {v.title}
              </h3>

              <p className="mt-3 text-sm leading-relaxed text-[#07130C]/55">
                {v.copy}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>

  {/* Gallery */}
  <section className="bg-[#F8FBF6] py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <ChapterHeading
        no="02"
        kicker="Life at Social Wavez"
        titleLines={["Team. Workspace. Real Work."]}
      />

      <div className="mt-14 columns-2 gap-5 md:columns-3 [&>figure]:mb-5">
        {gallery.map((g, i) => (
          <Reveal key={g.src} delay={(i % 3) * 0.08}>
            <figure
              className="group relative break-inside-avoid overflow-hidden rounded-3xl"
              data-testid={`gallery-item-${i}`}
            >
              <img
                src={g.src}
                alt={g.alt}
                loading="lazy"
                className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
              />

              <figcaption className="absolute bottom-4 left-4 rounded-full bg-[#07130C]/70 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/85 backdrop-blur-md">
                {g.label}
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>

  {/* Open Positions */}
  <section className="bg-white py-24 md:py-32">
    <div className="mx-auto max-w-4xl px-6 md:px-12">
      <ChapterHeading
        no="03"
        kicker="Open Positions"
        titleLines={["Where You Could Fit In."]}
      />

      <Reveal delay={0.15}>
        <Accordion
          type="single"
          collapsible
          className="mt-12 w-full"
        >
          {roles.map((r) => (
            <AccordionItem
              key={r.id}
              value={r.id}
              className="border-b border-[#07130C]/10"
            >
              <AccordionTrigger
                data-testid={`job-trigger-${r.id}`}
                className="py-6 text-left hover:no-underline"
              >
                <span className="flex flex-col gap-2 md:flex-row md:items-center md:gap-5">
                  <span className="font-display text-lg font-semibold text-[#07130C] md:text-xl">
                    {r.title}
                  </span>

                  <span className="flex gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#1C7543]/8 px-3 py-1 text-[11px] font-semibold text-[#1C7543]">
                      <Clock className="h-3 w-3" />
                      {r.type}
                    </span>

                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#07130C]/5 px-3 py-1 text-[11px] font-semibold text-[#07130C]/60">
                      <MapPin className="h-3 w-3" />
                      {r.location}
                    </span>
                  </span>
                </span>
              </AccordionTrigger>

              <AccordionContent className="pb-7">
                <p className="max-w-2xl text-sm leading-relaxed text-[#07130C]/60 md:text-base">
                  {r.description}
                </p>

                {/* DIRECT GMAIL LINK */}
                <a
                  href={getGmailUrl(r.title)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`job-apply-${r.id}`}
                  onClick={() => {
                    console.log(
                      "Opening Gmail application for:",
                      r.title
                    );

                    setRole(r.title);
                  }}
                  className="group mt-5 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#1C7543] to-[#D3B74D] px-6 py-2.5 text-sm font-semibold text-white transition-shadow duration-300 hover:shadow-[0_8px_28px_rgba(211,183,77,0.45)]"
                >
                  Apply for this role

                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>

  {/* Contact Section */}
  <section className="bg-[#F8FBF6] py-24 md:py-32">
    <div className="mx-auto max-w-3xl px-6 md:px-12 text-center">
      <ChapterHeading
        no="04"
        kicker="Don't See Your Role?"
        titleLines={["We Still Want to Meet You."]}
      />

      <Reveal delay={0.15}>
        <p className="mt-6 text-base leading-relaxed text-[#07130C]/60 md:text-lg">
          If you're exceptional at what you do, write to us directly at{" "}

          <a
            href={getGmailUrl("")}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-[#1C7543] underline decoration-[#D3B74D] underline-offset-4 hover:text-[#D3B74D]"
          >
            {hrEmail}
          </a>{" "}

          with your portfolio.
        </p>
      </Reveal>
    </div>
  </section>
</>


);
};

export default Career;
